// src/services/api.js
const API_URL = import.meta.env.VITE_APP_API_URL || 'http://localhost:3001';

/* ==============================|| JWT HELPERS ||============================== */

/**
 * Decodifica un JWT de forma segura, manejando Base64url y UTF-8.
 */
export const decodeJwt = (token) => {
  try {
    if (!token || typeof token !== 'string') return null;

    const parts = token.split('.');
    if (parts.length < 2) return null;

    let base64Url = parts[1];
    if (!base64Url) return null;

    // Base64url → Base64 estándar
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Agregar padding si falta (Base64 necesita longitud múltiplo de 4)
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );

    const decoded = atob(padded);

    // Manejar UTF-8 correctamente
    const jsonPayload = decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (err) {
    console.error('❌ Error decodificando JWT:', err);
    return null;
  }
};

/**
 * Devuelve true si el token expiró o es inválido.
 * Si NO se puede decodificar, asumimos que expiró (fail-safe).
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return true; // 👈 fail-safe: sin exp = expirado

  return Date.now() >= payload.exp * 1000 - 5000; // 5s de margen
};

/* ==============================|| SESIÓN ||============================== */

export const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

export const redirectToLogin = () => {
  // 👇 Evitar loops: si ya estamos en login, no redirigir
  if (window.location.pathname === '/login') return;

  clearSession();
  window.location.href = '/login';
};

/* ==============================|| FETCH ||============================== */

export async function apiFetch(endpoint, options = {}) {
  const token = localStorage.getItem('token');

  // Validación local de expiración (evita la request si ya venció)
  if (token && isTokenExpired(token)) {
    redirectToLogin();
    throw new Error('Sesión expirada. Vuelve a iniciar sesión.');
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (err) {
    // Error de red (no 401/403)
    throw new Error('Error de conexión con el servidor.');
  }

  // 401/403 → sesión inválida o expirada
  if (response.status === 401 || response.status === 403) {
    redirectToLogin();
    throw new Error('Sesión expirada o no autorizada.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = Array.isArray(errorData.message)
      ? errorData.message.join(', ')
      : errorData.message || `Error ${response.status}`;
    throw new Error(message);
  }

  // Respuestas sin cuerpo
  if (response.status === 204 || response.status === 205) return null;

  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

export { API_URL };